/**
 * Thanksgiving clipart — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const thanksgivingClipartPack: ToilePack = {
    slug: 'thanksgiving-clipart',
    category: 'season',
    name: 'Thanksgiving clipart',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'turkey', label: 'Turkey', subject: 'A turkey with a fanned tail seen side on, isolated on a plain white ground'},
        {key: 'pie', label: 'Pumpkin pie', subject: 'A whole pumpkin pie with a crimped crust, isolated on a plain white ground'},
        {key: 'acorn', label: 'Acorn', subject: 'A single acorn with its cap and an oak leaf, isolated on a plain white ground'},
    ],
    styles: [
        {key: 'harvest', label: 'Harvest flat', style: 'flat harvest illustration, simplified shapes, no outlines, matte colour', palette: 'Mustard, rust, pumpkin orange, olive and cream', framing: 'Centered on a solid cream background, built to read clean at small size'},
        {key: 'vintage', label: 'Vintage americana', style: 'vintage americana illustration, halftone grain, slight misregistration, warm', palette: 'Faded rust, brown, mustard and cream', framing: 'Centered on an aged cream paper background with an even margin'},
        {key: 'watercolor', label: 'Watercolor', style: 'soft watercolour illustration, loose edges, paper texture showing', palette: 'Warm autumn washes of orange, ochre and brown', framing: 'Centered on a plain white paper background with soft visible edges'},
    ],
    directions: ['a cornucopia instead', 'as a wreath border', 'in a single ink color'],
    directionHint: 'try: a cornucopia instead',
    seo: {
        title: 'Thanksgiving Clipart: Turkey, Pie & Acorn',
        metaDescription: 'Nine Thanksgiving clipart pieces — turkey, pumpkin pie, acorn — isolated on white in harvest flat, vintage americana, and watercolor styles.',
        h1: 'Thanksgiving clipart: turkey, pumpkin pie, acorn',
        tagline: 'Nine clipart pieces, three motifs, three treatments each.',
        intro: 'Nine Thanksgiving clipart images, each isolated clean on white: a turkey with a fanned tail, a whole pumpkin pie, and a single acorn with its cap, in a flat harvest illustration, a vintage americana halftone, and a soft watercolor treatment. Ready to drop straight into a newsletter, flyer, or worksheet.',
        useCases: [
            'a November newsletter or email header',
            'a classroom worksheet or bulletin board',
            'a recipe card or menu illustration',
            'a scrapbook or greeting-card layout',
        ],
        faq: [
            {q: 'Is the background transparent or white?', a: 'Each piece is isolated on a plain white background, ready to place directly or to knock out for a transparent version in any image editor.'},
            {q: 'Can I resize these without losing quality?', a: 'The files are delivered at a size suited to print and web use; for very large prints, scale conservatively to keep edges crisp.'},
            {q: "What's the difference between the three styles?", a: 'Harvest flat uses simplified matte shapes, vintage americana adds halftone grain and warm faded color, and watercolor uses soft loose edges with visible paper texture.'},
            {q: 'Can I use this clipart in a commercial newsletter or product?', a: 'Yes — the clipart is yours to use in personal and commercial projects, including newsletters, packaging, and printed goods.'},
        ],
    },
}
